import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout, { Container } from "../components/layout";
import { apiurl } from "../config/config";
import { FaFileExcel } from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import * as XLSX from "xlsx";
import { motion } from "framer-motion";
import { SuwasthiLoader } from "../components/loader";
import Pagination from "../components/pagination";

const Enquiries = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(0);

  const navigate = useNavigate();

  useEffect(() => {
    fetchEnquiries();
  }, [currentPage, itemsPerPage]);

  const fetchEnquiries = () => {
    setLoading(true);
    fetch(`${apiurl}/api/b2b/enquiry/get?page=${currentPage}&limit=${itemsPerPage}`)
      .then((res) => res.json())
      .then((data) => {
        setEnquiries(data.enquiries || []);
        setTotalItems(data.pagination?.totalItems || 0);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching enquiries:", err);
        setLoading(false);
      });
  };

  // 🔍 Filter by search + date range
  const filteredEnquiries = enquiries.filter((enq) => {
    const matchesSearch =
      enq?.name?.toLowerCase().includes(search.toLowerCase()) ||
      enq?.email?.toLowerCase().includes(search.toLowerCase());

    const enqDate = new Date(enq?.createdAt);
    const matchesDate =
      (!startDate || enqDate >= startDate) &&
      (!endDate || enqDate <= endDate);

    return matchesSearch && matchesDate;
  });

  const exportToExcel = () => {
    if (filteredEnquiries.length === 0) {
      alert("No enquiries found for selected filters");
      return;
    }
    const worksheet = XLSX.utils.json_to_sheet(filteredEnquiries);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Enquiries");
    XLSX.writeFile(workbook, "Enquiries.xlsx");
  };

  return (
    <Layout>
      <Container overflow={"overflow-scroll"}>
        <div className="p-2 bg-gray-50 min-h-screen">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800">📋 Enquiry List</h2>
            <button
              onClick={exportToExcel}
              className="flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-lg shadow hover:bg-green-600 transition"
            >
              <FaFileExcel /> Export
            </button>
          </div>

          {/* Filters */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <input
              type="text"
              placeholder="🔍 Search by name or email"
              className="p-3 border rounded-lg shadow-sm w-full md:w-1/3 focus:ring focus:ring-blue-200 outline-none"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <div className="flex items-center gap-2">
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                placeholderText="Start Date"
                className="p-2 border rounded-lg shadow-sm"
              />
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                placeholderText="End Date"
                className="p-2 border rounded-lg shadow-sm"
              />
            </div>

            <select
              className="p-2 border rounded-lg shadow-sm"
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
            >

              <option value="50">50</option>
              <option value="100">100</option>
              <option value="200">200</option>
              <option value="500">500</option>
            </select>
          </div>

          {/* Table */}
          {loading ? (
            <SuwasthiLoader />
          ) : (
            <div className="bg-white shadow-lg rounded-lg overflow-hidden">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-200 text-gray-700">
                    <th className="p-3 text-left">Enquiry ID</th>
                    <th className="p-3 text-left">Name</th>
                    <th className="p-3 text-left">Email</th>
                    <th className="p-3 text-left">Message</th>
                    <th className="p-3 text-left">Date</th>
                    <th className="p-3 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEnquiries.length > 0 ? (
                    filteredEnquiries.map((enq, index) => (
                      <motion.tr
                        key={enq._id}
                        className={`border-b hover:bg-gray-50 ${
                          index % 2 === 0 ? "bg-white" : "bg-gray-50"
                        }`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <td className="p-3">{enq?.id}</td>
                        <td className="p-3">{enq?.name}</td>
                        <td className="p-3">{enq?.email}</td>
                        <td className="p-3">{enq?.enquiry}</td>
                        <td className="p-3 text-gray-600">
                          {new Date(enq?.createdAt).toLocaleDateString()}
                        </td>
                        <td className="p-3">
                          <button
                            className="bg-blue-500 text-white px-3 py-1 rounded-lg shadow hover:bg-blue-600 transition"
                            onClick={() => navigate(`/enquiries/${enq._id}`)}
                          >
                            View
                          </button>
                        </td>
                      </motion.tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="6"
                        className="text-center py-6 text-gray-500"
                      >
                        No enquiries found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          <div className="mt-6">
            <Pagination
              currentPage={currentPage}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
        </div>
      </Container>
    </Layout>
  );
};

export default Enquiries;